import {
  useState, useEffect,
} from 'react';
import {
  useNavigate, Link,
} from 'react-router-dom';
import {
  Utensils, Eye, EyeOff 
} from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export function LoginPage() {
  const {
    signIn, isLoading, isAuthenticated, requiresNewPassword 
  } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Redirect to set password page if needed
  useEffect(() => {
    if (requiresNewPassword) {
      navigate('/set-password');
    }
  }, [requiresNewPassword, navigate]);

  const handleSubmit = (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    setIsSubmitting(true);
    void (async () => {
      try {
        const result = await signIn(email, password);
        
        if (result.status === 'success') {
          navigate('/');
        } else if (result.status === 'newPasswordRequired') {
          navigate('/set-password');
        } else if (result.status === 'resetPasswordRequired') {
          // Redirect to forgot password with email pre-filled
          navigate(`/forgot-password?email=${encodeURIComponent(email)}&reset=true`);
        }
        // MFA handling would go here
      } catch (err) {
        if (err instanceof Error) {
          // Map Cognito error messages to user-friendly messages
          if (err.message.includes('Incorrect username or password')) {
            setError('Invalid email or password');
          } else if (err.message.includes('User does not exist')) {
            setError('Invalid email or password');
          } else if (err.message.includes('Password attempts exceeded')) {
            setError('Too many failed attempts. Please try again later.');
          } else {
            setError(err.message);
          }
        } else {
          setError('Sign in failed');
        }
      } finally {
        setIsSubmitting(false);
      }
    })();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary">
            <Utensils className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">Welcome to GalleyOS</CardTitle>
          <CardDescription>Sign in to access the food supply chain platform</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium leading-none">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium leading-none">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            
            <Button type="submit" disabled={isSubmitting || isLoading} className="w-full">
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
          
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Contact your administrator if you need access
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
